import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import PropTypes from 'prop-types';

import { waterfallLabelSeparation } from 'src/helper/waterfallLabelSeparation';

import ButtonHelpModal from './components/ButtonHelpModal';

export const dataLabels = [
  'TotalNumberOrgUnits',
  'NotStarted',
  'Understand',
  'Dialog',
  'Action',
  'Review',
];

const FollowUpWaterfall = ({
  percentages = [],
  passedRef = [],
  totalCount = 0,
  cumulatedCount = [],
  bigText = false,
}) => {
  const { t } = useTranslation('planAction');
  const { colors } = useTheme();

  const chartData = useMemo(() => {
    const dummyData = [
      0,
      ...percentages.map((percentage, i) =>
        percentages.slice(i + 1).reduce((a, b) => a + b, 0),
      ),
    ];
    return [
      {
        data: dummyData,
        waterfall: {
          dummyStack: true,
        },
        stack: 'stack 0',
        label: 'dummyData',
        backgroundColor: 'transparent',
        dataLabels: {
          font: { size: bigText ? 25 : undefined },
        },
      },
      {
        data: [100, ...percentages],
        stack: 'stack 0',
        backgroundColor: [
          colors.neutral[500],
          colors.primary[500],
          colors.secondary[500],
          colors.secondary[500],
          colors.secondary[500],
          colors.secondary[500],
        ],
        label: 'data',
        dataLabels: {
          font: { size: bigText ? 25 : undefined },
        },
      },
    ];
  }, [bigText, percentages, colors]);

  const options = useMemo(
    () => ({
      legend: { display: false },
      maintainAspectRatio: false,
      responsive: true,
      tooltips: {
        callbacks: {
          title: (tooltipItems) =>
            tooltipItems[0]
              ? tooltipItems[0].xLabel.slice(
                  0,
                  tooltipItems[0].xLabel.length - 1,
                )
              : '',
          label: (tooltipItem) =>
            `${tooltipItem.yLabel}% - ${tooltipItem.xLabel[1].slice(2)}`,
        },
      },
      layout: {
        padding: {
          top: 50,
          bottom: 20,
          left: 20,
          right: 20,
        },
      },
      scales: {
        x: {
          axis: 'x',
          ticks: {
            fontColor: 'black',
            fontSize: bigText ? '25' : '16',
          },
        },
        y: {
          axis: 'y',
          display: false,
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      },
      plugins: {
        datalabels: {
          display: (context) => context.datasetIndex !== 0,
          formatter: (value) => `${value} %`,
          anchor: 'end',
          align: 'end',
          color: 'black',
          font: {
            size: bigText ? 30 : 24,
          },
        },
      },
    }),
    [bigText],
  );

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h5">
          {t('Statistics.Status.Waterfall.FollowUpStatus')}
        </Typography>
        <ButtonHelpModal />
      </Stack>
      <Box id="org-unit-waterfall" sx={{ minHeight: '30rem' }}>
        <Bar
          ref={passedRef}
          data={{
            labels: [totalCount, ...cumulatedCount].map((count, i) =>
              waterfallLabelSeparation(i, count, dataLabels, t),
            ),
            datasets: chartData,
          }}
          plugins={[ChartDataLabels]}
          options={options}
        />
      </Box>
    </Box>
  );
};

FollowUpWaterfall.propTypes = {
  percentages: PropTypes.arrayOf(PropTypes.number),
  passedRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  totalCount: PropTypes.number,
  cumulatedCount: PropTypes.arrayOf(PropTypes.number),
  bigText: PropTypes.bool,
};

export default FollowUpWaterfall;
